import React, { useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import ConsoleShell from '../../components/admin2/ConsoleShell';
import PhoneField from '../../components/PhoneField';
import {
  searchRequests, getRequest, searchClients, findClientByPhone, createClient, createDocument,
} from '../../lib/docgen/store';

// Document generator — step one: who is this document for?
//   1. an existing platform request (managed, "make your idea", any kind),
//   2. an offline WhatsApp client (doc_clients — NOT a platform request or
//      account; owner decision 2026-09-28, option C), or
//   3. nobody (standalone).
// Then the document type. Nothing here writes to `requests`: the request is
// only linked, and the offline client lives in the tool's own table.

const KIND_LABEL = {
  managed: { ar: 'مُدار', en: 'Managed' },
  idea_to_product: { ar: 'اصنع فكرتك', en: 'Make your idea' },
  factory: { ar: 'مصنع', en: 'Factory' },
  direct: { ar: 'مباشر', en: 'Direct' },
};

function useDebounced(value, ms = 300) {
  const [v, setV] = useState(value);
  useEffect(() => { const t = setTimeout(() => setV(value), ms); return () => clearTimeout(t); }, [value, ms]);
  return v;
}

export default function ConsoleDocNew({ user, profile, lang }) {
  const isAr = lang === 'ar';
  const t = (ar, en) => (isAr ? ar : en);
  const nav = useNavigate();
  const [params] = useSearchParams();

  const [mode, setMode] = useState('request'); // request | client | standalone
  const [request, setRequest] = useState(null);
  const [client, setClient] = useState(null);
  const [kind, setKind] = useState(params.get('kind') === 'rfq' ? 'rfq' : 'quotation');
  const [layout, setLayout] = useState('simple');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');

  // Opened from a request page: that request is already chosen.
  useEffect(() => {
    const id = params.get('request');
    if (!id) return;
    getRequest(id).then((r) => { if (r) { setRequest(r); setMode('request'); } }).catch(() => {});
  }, [params]);

  // ── request search ──
  const [rq, setRq] = useState('');
  const rqD = useDebounced(rq);
  const [reqs, setReqs] = useState(null);
  useEffect(() => {
    if (mode !== 'request') return;
    let alive = true;
    setReqs(null);
    searchRequests(rqD).then((r) => alive && setReqs(r)).catch((e) => alive && (setReqs([]), setErr(e.message)));
    return () => { alive = false; };
  }, [rqD, mode]);

  // ── client search / create ──
  const [cq, setCq] = useState('');
  const cqD = useDebounced(cq);
  const [clients, setClients] = useState(null);
  const [adding, setAdding] = useState(false);
  const [nc, setNc] = useState({ name: '', company: '', phone: '', e164: '', valid: false, notes: '' });
  const [notice, setNotice] = useState('');
  useEffect(() => {
    if (mode !== 'client') return;
    let alive = true;
    setClients(null);
    searchClients(cqD).then((r) => alive && setClients(r)).catch((e) => alive && (setClients([]), setErr(e.message)));
    return () => { alive = false; };
  }, [cqD, mode]);

  const addClient = async () => {
    setErr(''); setNotice('');
    if (!nc.name.trim()) { setErr(t('اسم العميل مطلوب.', 'Client name is required.')); return; }
    setBusy(true);
    try {
      // A returning client is picked, not created again.
      const existing = nc.e164 ? await findClientByPhone(nc.e164) : null;
      if (existing) {
        setClient(existing);
        setNotice(t(`هذا الرقم مسجل مسبقاً باسم «${existing.name}» — تم اختياره.`, `That number already belongs to “${existing.name}” — selected it.`));
      } else {
        setClient(await createClient({ name: nc.name, company: nc.company, phone: nc.phone, phone_e164: nc.e164, notes: nc.notes }));
      }
      setAdding(false);
    } catch (e) { setErr(e.message); }
    setBusy(false);
  };

  const target = mode === 'request' ? request : mode === 'client' ? client : true;
  const create = async () => {
    setErr('');
    setBusy(true);
    try {
      const id = await createDocument({
        kind, layout,
        request: mode === 'request' ? request : null,
        client: mode === 'client' ? client : null,
      });
      nav(`/admin2/docs/${id}`, { replace: true });
    } catch (e) { setErr(e.message); setBusy(false); }
  };

  const reqTitle = (r) => (isAr ? (r.title_ar || r.title_en) : (r.title_en || r.title_ar)) || t('طلب', 'Request');
  const buyer = (r) => r.profiles?.company_name || r.profiles?.full_name || '';
  const Pick = ({ on, onClick, children }) => (
    <button type="button" className="ac-card" onClick={onClick}
      style={{ display: 'block', width: '100%', textAlign: 'start', padding: 12, marginBottom: 8, cursor: 'pointer', font: 'inherit',
        outline: on ? '2px solid var(--ac-ink)' : 'none' }}>{children}</button>
  );

  return (
    <ConsoleShell user={user} profile={profile} lang={lang} active="docs">
      <div className="ac-page" style={{ fontFamily: isAr ? 'var(--font-ar)' : 'var(--font-sans)', maxWidth: 820 }}>
        <div className="ac-page-head">
          <div>
            <h1 className="ac-h1">{t('مستند جديد', 'New document')}</h1>
            <p className="ac-sub">{t('لمن هذا المستند؟', 'Who is this document for?')}</p>
          </div>
        </div>

        <div className="ac-tabs" style={{ marginTop: 0 }}>
          {[['request', t('طلب موجود', 'Existing request')], ['client', t('عميل واتساب', 'WhatsApp client')], ['standalone', t('بدون ربط', 'Standalone')]].map(([k, l]) => (
            <button key={k} className={`ac-tab${mode === k ? ' on' : ''}`} onClick={() => { setMode(k); setErr(''); setNotice(''); }}>{l}</button>
          ))}
        </div>

        {mode === 'request' && (
          <div>
            {request && (
              <div className="ac-card" style={{ padding: 12, marginBottom: 12, outline: '2px solid var(--ac-ink)' }}>
                <div style={{ fontSize: 12, color: 'var(--ac-muted)' }}>{t('الطلب المختار', 'Selected request')}</div>
                <div style={{ fontWeight: 700, marginTop: 4 }}>{request.request_ref} — {reqTitle(request)}</div>
                {buyer(request) && <div style={{ fontSize: 12.5, color: 'var(--ac-muted)', marginTop: 2 }}>{buyer(request)}</div>}
              </div>
            )}
            <input className="ac-input" style={{ marginBottom: 10 }} value={rq} onChange={(e) => setRq(e.target.value)}
              placeholder={t('ابحث برقم الطلب أو العنوان أو اسم العميل…', 'Search by reference, title or client name…')} />
            {reqs == null ? <div className="ac-skel" style={{ height: 60 }} /> : reqs.length === 0
              ? <div className="ac-placeholder">{t('لا نتائج.', 'No results.')}</div>
              : reqs.map((r) => (
                <Pick key={r.id} on={request?.id === r.id} onClick={() => setRequest(r)}>
                  <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                    <b>{r.request_ref}</b>
                    <span className="ac-badge neutral">{(KIND_LABEL[r.request_kind] || KIND_LABEL.direct)[isAr ? 'ar' : 'en']}</span>
                    {String(r.status).toLowerCase() === 'cancelled' && <span className="ac-badge warn">{t('ملغي', 'Cancelled')}</span>}
                  </div>
                  <div style={{ fontSize: 13, marginTop: 4 }}>{reqTitle(r)}</div>
                  {buyer(r) && <div style={{ fontSize: 12, color: 'var(--ac-muted)', marginTop: 2 }}>{buyer(r)}</div>}
                </Pick>
              ))}
            {request && String(request.status).toLowerCase() === 'cancelled' && (
              <div className="ac-placeholder" style={{ color: 'var(--ac-warn)' }}>
                {t('هذا الطلب ملغي. يمكنك المتابعة، فالمستند مستقل عن حالة الطلب.', 'This request is cancelled. You can continue — the document is independent of the request’s status.')}
              </div>
            )}
          </div>
        )}

        {mode === 'client' && (
          <div>
            {client && (
              <div className="ac-card" style={{ padding: 12, marginBottom: 12, outline: '2px solid var(--ac-ink)' }}>
                <div style={{ fontSize: 12, color: 'var(--ac-muted)' }}>{t('العميل المختار', 'Selected client')}</div>
                <div style={{ fontWeight: 700, marginTop: 4 }}>{client.name}{client.company ? ` — ${client.company}` : ''}</div>
                {client.phone && <div dir="ltr" style={{ fontSize: 12.5, color: 'var(--ac-muted)', marginTop: 2, textAlign: isAr ? 'right' : 'left' }}>{client.phone_e164 || client.phone}</div>}
              </div>
            )}
            {notice && <div className="ac-placeholder" style={{ color: 'var(--ac-info)' }}>{notice}</div>}
            {!adding ? (
              <>
                <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
                  <input className="ac-input" value={cq} onChange={(e) => setCq(e.target.value)}
                    placeholder={t('ابحث بالاسم أو الشركة أو الجوال…', 'Search by name, company or phone…')} />
                  <button className="ac-btn" onClick={() => { setAdding(true); setNotice(''); }}>{t('+ عميل جديد', '+ New client')}</button>
                </div>
                {clients == null ? <div className="ac-skel" style={{ height: 60 }} /> : clients.length === 0
                  ? <div className="ac-placeholder">{t('لا عملاء محفوظين بعد.', 'No saved clients yet.')}</div>
                  : clients.map((c) => (
                    <Pick key={c.id} on={client?.id === c.id} onClick={() => setClient(c)}>
                      <b>{c.name}</b>{c.company ? ` — ${c.company}` : ''}
                      {c.phone && <div dir="ltr" style={{ fontSize: 12, color: 'var(--ac-muted)', marginTop: 2, textAlign: isAr ? 'right' : 'left' }}>{c.phone_e164 || c.phone}</div>}
                    </Pick>
                  ))}
              </>
            ) : (
              <div className="ac-card" style={{ padding: 16 }}>
                <div className="ac-form-grid">
                  <label className="ac-field"><span className="ac-flabel">{t('الاسم *', 'Name *')}</span>
                    <input className="ac-input" value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} /></label>
                  <label className="ac-field"><span className="ac-flabel">{t('الشركة', 'Company')}</span>
                    <input className="ac-input" value={nc.company} onChange={(e) => setNc({ ...nc, company: e.target.value })} /></label>
                  <div className="ac-field wide"><span className="ac-flabel">{t('الجوال / واتساب', 'Phone / WhatsApp')}</span>
                    <PhoneField lang={lang} inputClassName="ac-input" selectClassName="ac-input"
                      onChange={(e164, valid) => setNc((s) => ({ ...s, phone: e164, e164: valid ? e164 : '', valid }))} /></div>
                  <label className="ac-field wide"><span className="ac-flabel">{t('ملاحظات', 'Notes')}</span>
                    <textarea className="ac-textarea" rows={2} value={nc.notes} onChange={(e) => setNc({ ...nc, notes: e.target.value })} /></label>
                </div>
                <div style={{ display: 'flex', gap: 8, marginTop: 12 }}>
                  <button className="ac-btn ac-btn-primary" disabled={busy} onClick={addClient}>{t('حفظ العميل', 'Save client')}</button>
                  <button className="ac-btn" onClick={() => setAdding(false)}>{t('إلغاء', 'Cancel')}</button>
                </div>
                <p style={{ fontSize: 11.5, color: 'var(--ac-muted)', marginTop: 10 }}>
                  {t('يُحفظ العميل داخل أداة المستندات فقط — لا يُنشأ له حساب ولا طلب على المنصة ولا تصله رسائل.',
                    'Saved inside the document tool only — no platform account or request is created, and no message is sent.')}
                </p>
              </div>
            )}
          </div>
        )}

        {mode === 'standalone' && (
          <div className="ac-placeholder">{t('مستند غير مرتبط بطلب أو عميل. يمكنك كتابة اسم العميل داخل النموذج.', 'Not linked to a request or client. You can type the client’s name in the form.')}</div>
        )}

        <h2 style={{ fontSize: 15, fontWeight: 700, margin: '26px 0 10px', color: 'var(--ac-ink)' }}>{t('نوع المستند', 'Document type')}</h2>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 10 }}>
          {[
            ['quotation', 'simple', t('عرض سعر — بسيط', 'Quotation — simple'), t('بنود بسعر لكل بند', 'Lines, each with a price')],
            ['quotation', 'itemized', t('عرض سعر — مفصّل بالأنظمة', 'Quotation — itemized'), t('أقسام بإجمالي لكل وحدة', 'Sections with a per-unit subtotal')],
            ['rfq', 'standard', t('طلب تسعير للمصنع (RFQ)', 'Supplier RFQ'), t('بالإنجليزية، بدون أسعار', 'English, no prices')],
          ].map(([k, l, title, sub]) => {
            const on = kind === k && (k === 'rfq' || layout === l);
            return (
              <Pick key={`${k}-${l}`} on={on} onClick={() => { setKind(k); if (k === 'quotation') setLayout(l); }}>
                <div style={{ fontWeight: 700 }}>{title}</div>
                <div style={{ fontSize: 12, color: 'var(--ac-muted)', marginTop: 3 }}>{sub}</div>
              </Pick>
            );
          })}
        </div>

        {err && <div className="ac-placeholder" style={{ color: 'var(--ac-danger)', marginTop: 12 }}>{err}</div>}
        <div className="ac-savebar">
          <button className="ac-btn" onClick={() => nav('/admin2/docs')}>{t('إلغاء', 'Cancel')}</button>
          <button className="ac-btn ac-btn-primary" disabled={busy || !target} onClick={create}>
            {busy ? '…' : t('إنشاء المسودة', 'Create draft')}
          </button>
        </div>
      </div>
    </ConsoleShell>
  );
}
